/**
 * « Subscriptions & Churn » — entrées et sorties d'abonnés, jour par jour.
 *
 * Même unité (des abonnements), donc un seul axe : on lit directement si les
 * entrées couvrent les sorties. Souscriptions en points reliés (rose), churn en
 * aire (jaune), essais créés en pointillé (bleu) : trois formes de marque
 * différentes, qui portent l'identité au-delà de la couleur (paire validée sur
 * la surface sombre, écart en deutéranopie dans la bande tolérée grâce à ce
 * double codage).
 *
 * Souscriptions = abonnements devenus payants ce jour-là ; churn =
 * résiliations demandées ce jour-là ; essais créés = essais gratuits démarrés
 * dans l'app ce jour-là (hors abonnements du site) — mêmes définitions que les
 * tuiles. Les essais ne comptent pas dans le solde : ce ne sont pas encore des
 * abonnés.
 * Par semaine au-delà de deux mois, par tranche de 30 jours au-delà d'un an.
 */

import type { SubscriptionDailyPoint } from '@shared/types/sales';
import { useMemo } from 'react';
import { Area, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { formatDayLong, formatDayShort, formatInteger } from '../lib/format';
import { bucketSizeFor } from '../lib/sales';
import { AXIS_STYLE, CATEGORICAL_COLORS, COLORS, LINE_WIDTH } from '../lib/theme';
import type { DataColumn } from '../types';
import { ChartCard } from './Card';
import { ChartLegend } from './ChartTooltip';
import { useChartMarkers } from '../hooks/useChartMarkers';
import { MarkerStrip } from './MarkerStrip';

const SUBSCRIPTIONS_COLOR = COLORS.series1;
const CHURN_COLOR = CATEGORICAL_COLORS[4];
const TRIALS_COLOR = CATEGORICAL_COLORS[1];

interface Bucket {
  date: string;
  subscriptions: number;
  churn: number;
  trials: number;
}

function bucketLabel(date: string, bucketSize: number): string {
  if (bucketSize === 1) return formatDayLong(date);
  return `${bucketSize === 7 ? 'Semaine' : '30 jours'} à partir du ${formatDayLong(date)}`;
}

function BucketTooltip({ active, payload, bucketSize }: { active?: boolean; payload?: Array<{ payload?: Bucket }>; bucketSize: number }) {
  const bucket = payload?.[0]?.payload;
  if (!active || !bucket) return null;
  const net = bucket.subscriptions - bucket.churn;
  return (
    <div className="tooltip" role="tooltip">
      <p className="tooltip__title">{bucketLabel(bucket.date, bucketSize)}</p>
      <div className="tooltip__row">
        <span className="legend__key" style={{ background: SUBSCRIPTIONS_COLOR }} aria-hidden="true" />
        <span className="tooltip__name">Souscriptions</span>
        <span className="tooltip__value">{formatInteger(bucket.subscriptions)}</span>
      </div>
      <div className="tooltip__row">
        <span className="legend__key legend__key--area" style={{ background: CHURN_COLOR }} aria-hidden="true" />
        <span className="tooltip__name">Churn</span>
        <span className="tooltip__value">{formatInteger(bucket.churn)}</span>
      </div>
      <div className="tooltip__row">
        <span
          className="legend__key legend__key--dashed"
          style={{ background: `repeating-linear-gradient(90deg, ${TRIALS_COLOR} 0 4px, transparent 4px 7px)` }}
          aria-hidden="true"
        />
        <span className="tooltip__name">Essais créés</span>
        <span className="tooltip__value">{formatInteger(bucket.trials)}</span>
      </div>
      <p className="tooltip__title">
        Solde : {net > 0 ? '+' : ''}
        {formatInteger(net)}
      </p>
    </div>
  );
}


/** Marqueurs « ventes » : impact lu sur les souscriptions. */
const MARKER_CONFIG = {
  value: (bucket: Bucket) => bucket.subscriptions,
  mode: 'level',
  unit: 'count',
  higherIsBetter: true,
  scope: 'sales',
} as const;

export function SubscriptionsChurnChart({ daily }: { daily: readonly SubscriptionDailyPoint[] }) {
  const bucketSize = bucketSizeFor(daily.length);

  const buckets = useMemo<Bucket[]>(() => {
    const out: Bucket[] = [];
    for (let start = 0; start < daily.length; start += bucketSize) {
      const slice = daily.slice(start, start + bucketSize);
      const first = slice[0];
      if (!first) continue;
      out.push({
        date: first.date,
        subscriptions: slice.reduce((sum, day) => sum + (day.newSubscribers ?? 0), 0),
        churn: slice.reduce((sum, day) => sum + (day.churned ?? 0), 0),
        trials: slice.reduce((sum, day) => sum + (day.trialsStarted ?? 0), 0),
      });
    }
    return out;
  }, [daily, bucketSize]);

  const markers = useChartMarkers(buckets, MARKER_CONFIG);

  const columns: ReadonlyArray<DataColumn<Bucket>> = [
    { key: 'date', header: bucketSize === 1 ? 'Jour' : 'Tranche', render: (row) => bucketLabel(row.date, bucketSize) },
    { key: 'subscriptions', header: 'Souscriptions', numeric: true, render: (row) => formatInteger(row.subscriptions) },
    { key: 'churn', header: 'Churn', numeric: true, render: (row) => formatInteger(row.churn) },
    { key: 'trials', header: 'Essais créés', numeric: true, render: (row) => formatInteger(row.trials) },
    {
      key: 'net',
      header: 'Solde',
      numeric: true,
      render: (row) => `${row.subscriptions - row.churn > 0 ? '+' : ''}${formatInteger(row.subscriptions - row.churn)}`,
    },
  ];

  const axisTick = { fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize };
  const hasData = buckets.some((bucket) => bucket.subscriptions > 0 || bucket.churn > 0 || bucket.trials > 0);
  const granularity = bucketSize === 1 ? 'par jour' : bucketSize === 7 ? 'par semaine' : 'par tranche de 30 jours';

  return (
    <ChartCard
      footer={<MarkerStrip items={markers.items} />}
      title="Subscriptions & Churn"
      subtitle={`Abonnements devenus payants, résiliations demandées et essais gratuits créés, ${granularity}`}
      className="chart-grid__wide"
      rows={hasData ? buckets : []}
      columns={columns}
      emptyMessage="Aucune souscription, résiliation ni essai sur cette période."
      chartHeight={300}
    >
      <ChartLegend
        entries={[
          { label: 'Souscriptions', color: SUBSCRIPTIONS_COLOR },
          { label: 'Churn', color: CHURN_COLOR, shape: 'area' },
          { label: 'Essais créés', color: TRIALS_COLOR, shape: 'dashed' },
        ]}
      />
      <ResponsiveContainer width="100%" height="86%">
        <ComposedChart data={buckets} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={COLORS.grid} vertical={false} />
          <XAxis dataKey="date" tickFormatter={formatDayShort} tick={axisTick} axisLine={{ stroke: COLORS.grid }} tickLine={false} minTickGap={32} />
          <YAxis tickFormatter={formatInteger} tick={axisTick} axisLine={false} tickLine={false} width={48} allowDecimals={false} />
          <Tooltip cursor={{ stroke: COLORS.textMuted, strokeWidth: 1 }} content={<BucketTooltip bucketSize={bucketSize} />} />
          {markers.lines}
          {/* L'aire d'abord : la courbe des souscriptions se dessine par-dessus. */}
          <Area
            type="monotone"
            dataKey="churn"
            name="Churn"
            stroke={CHURN_COLOR}
            strokeWidth={LINE_WIDTH}
            fill={CHURN_COLOR}
            fillOpacity={0.35}
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey="subscriptions"
            name="Souscriptions"
            stroke={SUBSCRIPTIONS_COLOR}
            strokeWidth={LINE_WIDTH}
            dot={buckets.length <= 62 ? { r: 4, fill: SUBSCRIPTIONS_COLOR, stroke: COLORS.surface, strokeWidth: 2 } : false}
            activeDot={{ r: 5, fill: SUBSCRIPTIONS_COLOR, stroke: COLORS.surface, strokeWidth: 2 }}
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey="trials"
            name="Essais créés"
            stroke={TRIALS_COLOR}
            strokeWidth={LINE_WIDTH}
            strokeDasharray="5 4"
            dot={false}
            activeDot={{ r: 5, fill: TRIALS_COLOR, stroke: COLORS.surface, strokeWidth: 2 }}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
